import { FieldArray, NumberField, SelectField, useFormField } from '@calendly/form';
import { FieldLabel } from '@calendly/form/parts';
import { I18n } from '@calendly/i18n';
import { IconButton } from '@calendly/ui/components/icon-button';
import { AddMiniIcon, CloseIcon } from '@calendly/ui/components/icons';
import { MinimalButton } from '@calendly/ui/components/minimal-button';
import { BodyText } from '@calendly/ui/components/typography';
import classNames from 'classnames';
import { nanoid } from 'nanoid';
import { forwardRef } from 'react';
import { useValidateOnChange } from '../hooks';
import { getTimingsLimit, getTimingType } from './config'; // i18n-tasks-use t('publisher.event_types.notifications.days_before')

// i18n-tasks-use t('publisher.event_types.notifications.days_before')
// i18n-tasks-use t('publisher.event_types.notifications.hours_before')
// i18n-tasks-use t('publisher.event_types.notifications.minutes_before')
// i18n-tasks-use t('publisher.event_types.notifications.days_after')
// i18n-tasks-use t('publisher.event_types.notifications.hours_after')
// i18n-tasks-use t('publisher.event_types.notifications.minutes_after')
// i18n-tasks-use t('publisher.event_type_editor.sections.communications.timings.units.days')
// i18n-tasks-use t('publisher.event_type_editor.sections.communications.timings.units.minutes')
// i18n-tasks-use t('publisher.event_type_editor.sections.communications.timings.units.hours')
// i18n-tasks-use t('publisher.event_type_editor.sections.more_options.notifications.timings.before_meeting')
// i18n-tasks-use t('publisher.event_type_editor.sections.more_options.notifications.timings.after_meeting')
const getTimingRelativeLabel = ({
  isAfter,
  useMeetingTimingLabels
}) => {
  if (useMeetingTimingLabels) {
    return isAfter ? I18n.t('publisher.event_type_editor.sections.more_options.notifications.timings.after_meeting') : I18n.t('publisher.event_type_editor.sections.more_options.notifications.timings.before_meeting');
  }
  return isAfter ? I18n.t('publisher.event_type_editor.sections.communications.timings.after_event') : I18n.t('publisher.event_type_editor.sections.communications.timings.before_event');
};
const getTimingLabel = (unit, timingType, useNewLabels) => {
  if (useNewLabels) {
    return I18n.t(`publisher.event_type_editor.sections.communications.timings.units.${unit}`);
  }
  const typeName = timingType === 'pre' ? 'before' : 'after';
  const i18nKey = `${unit}_${typeName}`;
  return I18n.t(`publisher.event_types.notifications.${i18nKey}`);
};
const containerCss = "cxhmo4e";
const Container = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, props.className)} />;
});
const selectWrapperCss = "s7bib62";
const SelectWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(selectWrapperCss, props.className)} />;
});
const wrapperUseNewLabelsCss = "wws95r1";
const removeButtonCss = "rblnv6s";
const wrapperCss = "w5ug097";
const Wrapper = forwardRef((props, ref) => {
  const {
    useNewLabels,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(wrapperCss, {
    [wrapperUseNewLabelsCss]: useNewLabels
  }, props.className)} />;
});
const RemoveButton = forwardRef((props, ref) => <IconButton ref={ref} {...props} className={classNames(removeButtonCss, props.className)} />);
const buildTimingOptions = (timingType, useNewLabels) => ['days', 'hours', 'minutes'].map(unit => ({
  id: unit,
  name: getTimingLabel(unit, timingType, useNewLabels)
}));
const Timing = ({
  name,
  disabled,
  timingType,
  onRemove,
  showRemove,
  useNewLabels,
  useMeetingTimingLabels = false,
  removeIcon: RemoveIcon = CloseIcon
}) => <Wrapper data-component={name} useNewLabels={useNewLabels}>
    <NumberField name={`${name}.interval`} disabled={disabled} maxLength="2" aria-label={I18n.t('publisher.event_types.notifications.interval')} data-1p-ignore="true" data-lpignore="true" />
    <SelectWrapper>
      <SelectField name={`${name}.unit`} disabled={disabled} aria-label={I18n.t('publisher.event_types.notifications.units')} options={buildTimingOptions(timingType, useNewLabels)} />
      {useNewLabels && <div>
            {getTimingRelativeLabel({
        isAfter: timingType !== 'pre',
        useMeetingTimingLabels
      })}
          </div>}
    </SelectWrapper>
    {showRemove && <RemoveButton title={I18n.t('publisher.action.remove')} onClick={onRemove} disabled={disabled}>
        <RemoveIcon />
      </RemoveButton>}
  </Wrapper>;
export const Timings = ({
  name,
  disabled,
  defaultValue,
  eventType,
  useNewLabels = false,
  useMeetingTimingLabels = false,
  etLocked = false,
  removeIcon
}) => {
  const [{
    value: {
      kind,
      timings
    }
  }] = useFormField(name);
  const [,, {
    setTouched
  }] = useFormField(`${name}.timings`);
  useValidateOnChange(`${name}.timings`);
  if (etLocked) {
    return <div>
        <FieldLabel text={I18n.t('publisher.event_types.notifications.timing')} />
        <BodyText>
          {timings[0].interval}
          &nbsp;
          {timings[0].unit}
          &nbsp;
          {getTimingRelativeLabel({
          isAfter: kind === 'invitee_email_follow_up',
          useMeetingTimingLabels: useNewLabels && useMeetingTimingLabels
        })}
        </BodyText>
      </div>;
  }
  return <Container>
      <FieldLabel text={I18n.t('publisher.event_types.notifications.timing')} />
      <FieldArray name={`${name}.timings`} render={({
      push,
      remove
    }) => <>
            {timings.map((timing, index) => <Timing key={timing.id} disabled={disabled} name={`${name}.timings.${index}`} timingType={getTimingType(kind)} onRemove={() => {
        remove(index);
        setTouched(true);
      }} showRemove={timings.length > 1} useNewLabels={useNewLabels} useMeetingTimingLabels={useMeetingTimingLabels} removeIcon={removeIcon} />)}
            {timings.length < getTimingsLimit(kind, eventType) && <MinimalButton fontSize="medium" disabled={disabled} onClick={() => {
        push({
          id: nanoid(5),
          ...defaultValue
        });
        setTouched(true);
      }}>
                <AddMiniIcon />
                {useNewLabels ? I18n.t('publisher.event_type_editor.sections.communications.timings.add_reminder') : I18n.t('publisher.event_types.notifications.add_reminder')}
              </MinimalButton>}
          </>} />
    </Container>;
};

require("./timings.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./timings.js");